import { SerializedStyles } from "@emotion/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Ref, memo, useCallback } from "react";
import { useForm } from "react-hook-form";
import { FormFlowPage, TextInput } from "src/components";
import useMergedRef from "src/hooks/useMergedRef";
import * as z from "zod";

import { usePartialSubmit } from "../hooks";
import { styles } from "../styles";

export interface ApplicantNameProps {
  value?: string;
  onChange?: (value?: string) => void;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

const SCHEMA = z.object({
  applicantName: z.string().min(5, {
    message: "Please enter both your first and last names.",
  }),
});

const ApplicantName = ({
  value,
  onChange,
  onNext,
  onBack,
  style,
  focusRef,
  ...props
}: ApplicantNameProps) => {
  const validate = useCallback(() => !!value, [value]);
  const { submitHandler, nextDisabled, nextLoading } = usePartialSubmit({ onNext, validate });

  const { register, handleSubmit, errors } = useForm({
    resolver: zodResolver(SCHEMA),
  });

  const merged = useMergedRef(register, focusRef);
  const ref = focusRef ? merged : register;

  return (
    <FormFlowPage
      {...props}
      title="What's your name?"
      description="Let's get to know each other before we connect on your application."
      onNext={handleSubmit(submitHandler)}
      onBack={onBack}
      style={style}
      nextDisabled={nextDisabled}
      nextLoading={nextLoading}
    >
      <TextInput
        error={errors?.applicantName?.message}
        ref={ref}
        style={styles.input}
        name="applicantName"
        label="Full name"
        placeholder="John Smith"
        defaultValue={value || ""}
        onChange={onChange}
        autoComplete="name"
      />
    </FormFlowPage>
  );
};

export default memo(ApplicantName);
